// ─────────────────────────────────────────────────────────────
// Navigation — Screen and tab management + URL hash routing
// ─────────────────────────────────────────────────────────────
import { SESSION_KEYS } from '@/constants';
import { logPageView } from '@/services/logger.service';
import type { ScreenId, TabId } from '@/types';

export const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'overview',   label: 'Início',   icon: '🏠' },
  { id: 'roteiro',    label: 'Roteiro',  icon: '🗺️' },
  { id: 'gastos',     label: 'Gastos',   icon: '💸' },
  { id: 'planejar',   label: 'Planejar', icon: '📋' },
  { id: 'documentos', label: 'Docs',     icon: '📄' },
  { id: 'exportar',   label: 'Guia em PDF', icon: '📕' },
  { id: 'viagem',     label: 'Config.',  icon: '⚙️' },
];

const PRIMARY_COUNT = 5;

const VALID_TABS = new Set<string>(TABS.map(t => t.id));
const _tabCallbacks = new Map<TabId, () => void>();
let _currentTab: TabId = 'overview';

export function getActiveTab(): TabId { return _currentTab; }

export function onTabActivate(tabId: TabId, cb: () => void): void {
  _tabCallbacks.set(tabId, cb);
}

export function showScreen(name: ScreenId): void {
  document.querySelectorAll('.screen').forEach(s => s.classList.remove('active'));
  document.getElementById(`screen-${name}`)?.classList.add('active');
  const bnav = document.getElementById('bottom-nav-app');
  if (bnav) bnav.style.display = name === 'app' ? '' : 'none';
  if (name === 'trips') history.pushState(null, '', '#trips');
  else if (name === 'auth') history.replaceState(null, '', '#auth');
  // The app screen is represented more precisely by its active trip tab.
  if (name !== 'app' && name !== 'admin' && name !== 'auth' && name !== 'loading') {
    logPageView(`tela:${name}`, { screen: name });
  }
}

export function switchTab(tabId: TabId): void {
  _currentTab = tabId;
  document.querySelectorAll('.nav-tab').forEach(el => {
    el.classList.toggle('active', (el as HTMLElement).dataset.tab === tabId);
  });

  const primaryIds = new Set(TABS.slice(0, PRIMARY_COUNT).map(t => t.id));
  const isOverflow = !primaryIds.has(tabId);
  document.querySelectorAll('.bnav-item').forEach(el => {
    if (el.id === 'bnav-more') el.classList.toggle('active', isOverflow);
    else el.classList.toggle('active', el.id === `bnav-${tabId}`);
  });

  document.querySelectorAll('.tab-section').forEach(s => s.classList.remove('active'));
  document.getElementById(`tab-${tabId}`)?.classList.add('active');
  sessionStorage.setItem(SESSION_KEYS.CURRENT_TAB, tabId);
  history.pushState({ tab: tabId }, '', `#tab-${tabId}`);
  _tabCallbacks.get(tabId)?.();
  if (document.getElementById('screen-app')?.classList.contains('active')) {
    const label = TABS.find(tab => tab.id === tabId)?.label ?? tabId;
    logPageView(`viagem:${tabId}`, { screen: 'app', tab: tabId, label });
  }
}

export function restoreTabFromSession(): TabId {
  const saved = sessionStorage.getItem(SESSION_KEYS.CURRENT_TAB) as TabId | null;
  const hash  = window.location.hash.replace('#tab-', '') as TabId;
  return (VALID_TABS.has(hash) ? hash : VALID_TABS.has(saved ?? '') ? saved : 'overview') as TabId;
}

export function initHistoryListener(): void {
  window.addEventListener('popstate', (e) => {
    const hash = window.location.hash;
    if (hash === '#trips' || hash === '' || hash === '#auth') {
      (window as unknown as Record<string, (s: string) => void>)['__showScreen']?.('trips');
      return;
    }
    if (hash.startsWith('#tab-')) {
      const tabId = hash.replace('#tab-', '') as TabId;
      if (VALID_TABS.has(tabId)) switchTab(tabId);
    }
    if (e.state?.tab && VALID_TABS.has(e.state.tab)) switchTab(e.state.tab as TabId);
  });
}

export function buildNavTabs(): string {
  return TABS.map(t =>
    `<button class="nav-tab" data-tab="${t.id}" onclick="window.__switchTab('${t.id}')">${t.label}</button>`
  ).join('');
}

export function buildBottomNav(): string {
  const primary = TABS.slice(0, PRIMARY_COUNT);
  const overflow = TABS.slice(PRIMARY_COUNT);

  const primaryHtml = primary.map(t =>
    `<button class="bnav-item" id="bnav-${t.id}" aria-label="${t.label}" onclick="window.__switchTab('${t.id}')">
      <span class="bnav-icon">${t.icon}</span>${t.label}
    </button>`
  ).join('');

  if (!overflow.length) return primaryHtml;

  const moreBtn = `<button class="bnav-item" id="bnav-more" aria-label="Mais opções" onclick="window.__toggleMoreDrawer()">
    <span class="bnav-icon bnav-more-dots">···</span>Mais
  </button>`;
  return primaryHtml + moreBtn;
}

export function buildMoreDrawerItems(): string {
  return TABS.slice(PRIMARY_COUNT).map(t =>
    `<button class="bnav-drawer-btn" data-tab="${t.id}" onclick="window.__switchTab('${t.id}');window.__closeMoreDrawer()">
      <span class="bnav-drawer-icon">${t.icon}</span>${t.label}
    </button>`
  ).join('');
}

export function openMoreDrawer(): void {
  document.getElementById('bnav-more-overlay')?.classList.add('open');
  document.getElementById('bnav-more-sheet')?.classList.add('open');
}

export function closeMoreDrawer(): void {
  document.getElementById('bnav-more-overlay')?.classList.remove('open');
  document.getElementById('bnav-more-sheet')?.classList.remove('open');
}

export function toggleMoreDrawer(): void {
  const sheet = document.getElementById('bnav-more-sheet');
  if (sheet?.classList.contains('open')) closeMoreDrawer();
  else openMoreDrawer();
}
